import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

import { Container, Button, Row, Col, OverlayTrigger, Tooltip, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { ContentHeader, ContentBody } from "../styles";
import { InputNumber } from "primereact/inputnumber";
import { CardAccount } from "../../components/card-account";
import { Modal } from "../../../../../../../components/modal";
import { FormAccount } from "../../components/form-account";
import Error from "../../../../../../../components/Error";

import { HiOutlineArrowCircleLeft } from "react-icons/hi";
import { formatReal } from "../../../../../../../../helpers";
import { NEW_WITHDRAWAL_STEPS } from "../../../../../../../@enums";
import { getBankAccounts, removeAccountBank } from "../../../../../../../services/manager/partner/withdrawl.service";

export const RequestWithdrawal = (props: any): JSX.Element => {
  const history = useHistory();
  const { t: translation } = useTranslation();
  const { balance, withdrawalValue, setWithdrawalValue, addAccountSelected, setIndexStep } = props;
  const validationSchema = Yup.object({
    withdrawalValue: Yup.number()
      .nullable()
      .max(balance, "Digite um valor menor que o saldo disponível")
      .required("Este campo é obrigatório"),
  }).required();
  const { handleSubmit, setValue, control, formState: { errors: errors }} = useForm({ resolver: yupResolver(validationSchema) });

  const [showFormAccount, setShowFormAccount] = useState<boolean>(false);
  const [showModalRemoveAccount, setShowModalRemoveAccount] = useState<boolean>(false);
  const [showSelectAccountMessage, setShowSelectAccountMessage] = useState<boolean>(false);
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [idAccountToRemove, setIdAccountToRemove] = useState<number>(0);
  const [accountSelected, setAccountSelected] = useState("");
  const [accountSelectedEdit, setAccountSelectedEdit] = useState<any>();
  const [accounts, setAccounts] = useState([]);

  const onSubmit = (formData: any) => {
    if (!accountSelected) {
      setShowSelectAccountMessage(true);
    } else {
      setWithdrawalValue(parseFloat(formData.withdrawalValue));
      setIndexStep(NEW_WITHDRAWAL_STEPS.confirmWithdrawal);
    }
  };

  const handleEditAccount = (account: any) => {
    setAccountSelectedEdit(account);
    setShowFormAccount(true);
  };

  const handleSelectAccount = (account: any) => {
    addAccountSelected(account);
    setAccountSelected(account.account);
  };

  const handleRemoveAccount = (idAccount: number) => {
    setIdAccountToRemove(idAccount);
    setShowModalRemoveAccount(true);
  };

  const handleButtonRemoveAccount = () => {
    setLoadingModal(true);
    removeAccountBank(idAccountToRemove).then(() => {
      let arr: any = accounts;
      arr = arr.filter((account: any) => account.id !== idAccountToRemove);
      setAccounts(arr);
      setShowModalRemoveAccount(false);
      setLoadingModal(false);
      toast.success('Conta removida com sucesso!')
    }).catch((err: any) => {
      toast.error('Erro ao remover sua conta bancária. Tente novamente.')
      setLoadingModal(false);
      setShowModalRemoveAccount(false);
    })
  };

  const getBanks = () => {
    getBankAccounts().then((data) => {
      setAccounts(data);
      if (data.length === 0) {
        setShowFormAccount(true)
      }
    });
  }

  useEffect(() => {
    if (!showFormAccount) {
      getBanks();
      setAccountSelectedEdit("")
    }
  }, [showFormAccount]);

  useEffect(() => {
    if (accountSelected) {
      setShowSelectAccountMessage(false);
    }
  }, [accountSelected]);

  useEffect(() => {
    getBanks();

    if (withdrawalValue > 0) {
      setValue("withdrawalValue", withdrawalValue);
    } else {
      setValue("withdrawalValue", null);
    }
  }, []);

  return (
    <Container fluid>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ContentHeader>
          <Row>
            <Col>
              <div className="title">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{translation("back")}</Tooltip>}
                >
                  <span>
                    <HiOutlineArrowCircleLeft
                      className="mr-1 mb-1 cursor-pointer"
                      onClick={() =>
                        history.push("/manager/partner/withdrawal")
                      }
                    />
                  </span>
                </OverlayTrigger>
                {translation("manager.withdrawal.request")}
              </div>
            </Col>
            <Col className="text-end">
              <Button
                className="mb-1"
                type="submit"
                disabled={accounts.length === 0 ? true : false}
              >
                {translation("manager.withdrawal.request")}
              </Button>
            </Col>
          </Row>
        </ContentHeader>

        <h5 className="mt-4 text-black">
          <b>{translation("manager.withdrawal.withdrawalValue")}</b>
        </h5>
        <p className="">
          {translation("manager.withdrawal.balanceAvailable")}: &nbsp;
          <span className="text-green-dstore">
            <b>R$ {balance > 0 ? formatReal(balance.toFixed(2)) : "0,00"}</b>
          </span>
        </p>
        <Col xs={12} className="mb-4">
          <Row>
            <Col md={12} xl={8} xxl={6}>
              <Controller
                name="withdrawalValue"
                control={control}
                rules={{ required: true }}
                defaultValue={withdrawalValue}
                render={({ field }) => (
                  <>
                    <InputNumber
                      value={field.value}
                      onValueChange={field.onChange}
                      className="input-currency w-100"
                      placeholder="R$ 0,00"
                      mode="currency"
                      currency="BRL"
                      locale="pt-BR"
                    />
                    {errors.withdrawalValue ? (
                      <div className="w-100">
                        {" "}
                        <Error>{errors.withdrawalValue?.message}</Error>{" "}
                      </div>
                    ) : (
                      ""
                    )}
                  </>
                )}
              />

              <Button
                className="text-start mt-3"
                variant="link"
                onClick={() => setShowFormAccount(true)}
                style={{ textDecoration: "none", padding: 0 }}
              >
                {translation("manager.withdrawal.newAccount")}
              </Button>
            </Col>
          </Row>
        </Col>
      </Form>

      <ContentBody>
        <Row>
          {showFormAccount ? (
            <Row>
              <Col md={12} xl={8} xxl={6}>
                <FormAccount
                  account={accountSelectedEdit}
                  handleCancelEdit={setShowFormAccount}
                />
              </Col>
            </Row>
          ) : (
            <>
              {accounts.length > 0 &&
                accounts.map((account: any, index: number) => (
                  <Col key={index} md={12} xl={4} xxl={3}>
                    <CardAccount
                      account={account}
                      handleSelectAccount={handleSelectAccount}
                      accountSelected={accountSelected}
                      handleRemoveAccount={handleRemoveAccount}
                      handleEditAccount={handleEditAccount}
                    />
                  </Col>
                ))}
              {showSelectAccountMessage ? (
                <div className="w-100">
                  <Error>Você precisa selecionar uma conta</Error>
                </div>
              ) : (
                ""
              )}
            </>
          )}
        </Row>
      </ContentBody>

      <Modal
        headerTitle="Aviso"
        submitButtonText={translation("remove")}
        show={showModalRemoveAccount}
        onHide={() => setShowModalRemoveAccount(false)}
        onSubmit={handleButtonRemoveAccount}
        variant="confirm-modal"
        loading={loadingModal}
      >
        <p>Tem certeza que deseja remover esta conta?</p>
      </Modal>
    </Container>
  );
};
